import { Dialog } from "@headlessui/react";
import { useEffect, useRef, useState } from "react";
import Button from "../../components/button/Button";
import { Label, TextField } from "../../components/fields/Fields";
import { Modal } from "../../components/modal/Modal";
import { ModalProp } from "../../components/modal/Modal";
import { RadioButtonMessage, RadioButtonItem } from "../../components/radiobutton/RadioButtonMessage";
import { Messages, messageSchema, newMessage, Token } from "../../model/Message";
import { useInput } from "../../hooks/useInput";
import TableList, { GridCol, GridRow } from "../../components/table/TableList";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import FormController from "../../components/fields/FormController";
import { FormBox, ModalFoterBox } from "../../components/box/Box";
import { Company } from "../../model/Companies";
import { ReplyButton } from "../../model/ReplyButton";
import { OnlineUrls } from "../../model/OnlineUrls";
import { SelectFields } from "../../components/select/SelectFields";
import InputFile from "../../components/input/InputFile";
import { useLocation } from "react-router-dom";
import QuestionnaireModal, { QuestionnaireForm } from "./QuestionnaireModal";
import { Questionnaire } from "../../model/Questionnaire";
import MessagePointSettingModal, { PointForm } from "./MessagePointSettingModal";
import companiesQuery from "../../firestore/Companies.query";
import { auth } from "../../firebase";
import { checkOption, isOptionActive } from "../../controller/OptionController";
import { useDispatch } from "react-redux";
import { alertOpen, AlertValue } from "../../features/alert.slice";

export type MessageProps = {
  message?: Messages; // お知らせデータ
  company?: Company; // 団体情報
  selectedColumns: GridCol[];
  selectedRowList: GridRow[];
  onClickPreview: (data: Messages) => Promise<void>; // プレビュー画面へデータを渡す
  onDraft: (data: Messages) => Promise<void>; // 下書き保存
  selectedEventType?: string; // 選択状態のイベントタイプ
  onEventTypeSelect: (eventType: string) => void; // イベントタイプ選択(セレクトボックスの状態取得用)
  replyButtonSelected?: ReplyButton; // 選択状態の返信ボタン
  onReplyButtonSelect: (id: string) => void; // 返信ボタン選択(セレクトボックスの状態取得用)
  replyButtonList?: ReplyButton[]; // 返信ボタンリスト
  defaultReplyButtonList?: ReplyButton[]; // 通常の返信ボタンリスト
  eventReplyButtonList?: ReplyButton[]; // イベントの返信ボタンリスト
  setReplyButtonList: React.Dispatch<React.SetStateAction<ReplyButton[]>>; // 返信ボタンの書き換えステート
  onlineUrlList?: OnlineUrls[]; // ミーティングURLリスト
  onlineUrlSelected?: string; // 選択状態のミーティングURL
  onOnlineUrlSelect: (id: string) => void; // ミーティングURL選択(セレクトボックスの状態取得用)
  tokenList?: Token[]; // 個別配信トークンリスト
  setTopicState: React.Dispatch<React.SetStateAction<string[]>>; // グループ配信リストステート
  onSendImage: (file: File) => void; // 画像登録
  base64Image: string; // 画像データ
  setBase64Image: React.Dispatch<React.SetStateAction<string>>; // 画像データステート
  setIsGroupSelectOpen: React.Dispatch<React.SetStateAction<boolean>>; // グループ選択モーダル開閉ステート
  setIsUserSelectOpen: React.Dispatch<React.SetStateAction<boolean>>; // 利用者選択モーダル開閉ステート
  setTokenState: React.Dispatch<React.SetStateAction<string[]>>; // 個別配信トークン選択ステート
  onQuestionnaire: (data: QuestionnaireForm) => void; // アンケートデータの設定関数を追加
  getQuestionnaire?: Questionnaire;
};

export const MessageEditModal = ({
  open, toggle, closeOnClickOutside,
  selectedColumns, selectedRowList,
  message = newMessage(),
  onClickPreview, onDraft, setTokenState, setTopicState,
  onReplyButtonSelect,
  onOnlineUrlSelect, onlineUrlSelected,
  onEventTypeSelect, selectedEventType = '',
  onSendImage, setBase64Image, base64Image = '',
  tokenList, company,
  replyButtonSelected,
  replyButtonList,
  setReplyButtonList,
  defaultReplyButtonList = [],
  eventReplyButtonList = [],
  onlineUrlList,
  setIsGroupSelectOpen,
  setIsUserSelectOpen,
  onQuestionnaire,
  getQuestionnaire,
}: ModalProp & MessageProps) => {
  const location = useLocation();
  const dispatch = useDispatch();

  const { control, trigger, handleSubmit, watch, getValues, setValue, unregister, reset } =
    useForm<Messages & { select: string, topicLength: number, tokenLength: number }>({ resolver: yupResolver(messageSchema), defaultValues: message });

  const setRadioItem = () => {
    let radio: RadioButtonItem[] = [];
    if (!getValues("eventFlg")) {
      radio.push({
        id: company?.displayId!,
        title: "全体",
      })
    }
    radio.push({
      id: "topic",
      title: "グループ配信",
    },
    {
      id: "token",
      title: "個別配信",
    });
    return radio;
  }
  const radioItem: RadioButtonItem[] = setRadioItem();
  const select = useInput('');

  // submit押下フラグ
  const submitProcessing = useRef(false);

  // ログイン中のユーザー情報
  const currentUser = auth.currentUser;

  const [optionFlg, setOptionFlg] = useState<{ point: boolean }>({
    point: false
  });
  
  // オプション設定出し分け
  async function fetchOptions() {
    const options = await companiesQuery.getOptionContractList(currentUser?.tenantId!);
    // ポイント管理のステータス、期間チェック
    const pointOption = options.find((option) => option.optionId === 'point');
    const point = pointOption ? isOptionActive(pointOption) : false;
    return { point };
  }

  useEffect(() => {
    async function loadOptions() {
      const result = await fetchOptions();
      setOptionFlg(result);
    }
  
    loadOptions();
  }, []);

  useEffect(() => {
    // オプションが無効なとき、ポイント設定を初期化する
    if (!optionFlg["point"]) {
      setValue('pointSettingFlg', false);
      setValue('pointId', "");
      setValue('pointTypeId', "");
      setValue('pointTypeName', "");
      setValue('pointName', "");
      setValue('pointAssignmentPoint', 0);
      setValue('pointCategoryId', 0);
      setValue('pointCategoryName', "");
      setValue('pointAwardedFlg', false);
    }
  }, [optionFlg]);

  const onValidSubmit = async (formData: Messages) => {
    if (formData.pointSettingFlg) {
      const pointEnabled = await checkOption("point", dispatch, false);
      if (!pointEnabled) {
        dispatch(
          alertOpen(
            AlertValue(
              "ポイント管理のオプションが無効のため、ポイントを設定できません。",
              "danger"
            )
          )
        );
        return;
      }
    }
    await onClickPreview(formData);
  };

  // ポイント設定押下
  const [isMessagePointSettingModalOpen, setMessagePointSettingModalOpen] = useState(false);
  const pointModalRef = useRef<{ resetForm: () => void }>(null);
  const openMessagePointSettingModal = () => setMessagePointSettingModalOpen(true);
  const closeMessagePointSettingModal = () => setMessagePointSettingModalOpen(false);
  const modalContentRef = useRef<HTMLDivElement>(null);

  const handlePointSettingSubmit = (data: PointForm) => {
    setValue('pointSettingFlg', true);
    setValue('pointId', data.id);
    setValue('pointTypeId', data.typeId);
    setValue('pointTypeName', data.typeName);
    setValue('pointName', data.pointName);
    setValue('pointAssignmentPoint', data.point);
    setValue('pointCategoryId', data.categoryId);
    setValue('pointCategoryName', data.categoryName);
    setValue('pointAwardedFlg', false);
    closeMessagePointSettingModal();
  };

  const handlePointDelete = () => {
    setValue('pointSettingFlg', false);
    setValue('pointId', "");
    setValue('pointTypeId', "");
    setValue('pointTypeName', "");
    setValue('pointName', "");
    setValue('pointAssignmentPoint', 0);
    setValue('pointCategoryId', 0);
    setValue('pointCategoryName', "");
    setValue('pointAwardedFlg', false);
    if (pointModalRef.current) {
      pointModalRef.current.resetForm();
    }
  };
  
  const pointSettingFlg = watch('pointSettingFlg');
  useEffect(() => {
    if (isMessagePointSettingModalOpen && modalContentRef.current) {
      modalContentRef.current.focus();
    }
  }, [isMessagePointSettingModalOpen]);

  // アンケート設定押下
  const [isQuestionnaireModalOpen, setQuestionnaireModalOpen] = useState(false);
  const [questionnaireData, setQuestionnaireData] = useState<QuestionnaireForm | undefined>(undefined);
  const questionnaireModalRef = useRef<{ resetForm: () => void }>(null);
  const [eventFlgResult, setEventFlgResult] = useState(false);

  const openQuestionnaireModal = () => setQuestionnaireModalOpen(true);
  const closeQuestionnaireModal = () => setQuestionnaireModalOpen(false);

  const handleQuestionnaireSubmit = (data: QuestionnaireForm) => {
    setQuestionnaireData(data);
    onQuestionnaire(data); 
    setValue('questionnaireFlg', true);
    closeQuestionnaireModal();
  };

  const handleQuestionnaireDelete = () => {
    setQuestionnaireData(undefined);
    setValue('questionnaireFlg', false);
    if (questionnaireModalRef.current) {
      questionnaireModalRef.current.resetForm();
    }
  };

  useEffect(() => {
    if (isQuestionnaireModalOpen && modalContentRef.current) {
      modalContentRef.current.focus();
    }
  }, [isQuestionnaireModalOpen]);

  useEffect(() => {
    // 予定表からの新規登録を判定
    const eventCreateFlg = message.eventFlg && message.eventAt !== undefined && message.id === "";

    // 下書き または 予定表新規 または 配信予定日が未来日(未配信含む) (新規は除外)
    if(message.draftFlg || eventCreateFlg || message.sendDate > new Date()) {

      // 個別配信トークンをセット
      if(message.select==='token'|| select.value==='token'){
      const tokenStateInit = tokenList?.filter((token) => token.messageId === message.id)
        .map((token) => token.userId) ?? [];
      setTokenState(tokenStateInit);
      }else{
        setTokenState([]);
      }

      // 入力項目に値をセット
      setValue('id', message.id);
      setValue('body', message.body);
      setValue('urlButtonFlg', message.urlButtonFlg ?? false);
      setValue('urlButtonName', message.urlButtonName ?? '');
      setValue('urlLink', message.urlLink ?? '');
      setValue('title', message.title);
      setValue('contactInformation', message.contactInformation);
      setValue('eventVenue', message.eventVenue);
      setValue('image', message.image);
      setValue('type', message.type);
      setValue('link', message.link);
      setValue('fileupload', message.fileupload);
      setValue('draftFlg', message.draftFlg);
      setValue('authorUid', message.authorUid);
      setValue('deliveredFlg', message.deliveredFlg);
      setValue('eventFlg', message.eventFlg);
      setValue('eventType', message.eventType);
      setValue('url', message.url ?? 'https://');
      setValue('questionnaireFlg', message.questionnaireFlg);
      setEventFlgResult(message.eventFlg);
      setValue('pointSettingFlg', message.pointSettingFlg);
      setValue('pointId', message.pointId);
      setValue('pointTypeId', message.pointTypeId);
      setValue('pointTypeName', message.pointTypeName);
      setValue('pointName', message.pointName);
      setValue('pointAssignmentPoint', message.pointAssignmentPoint);
      setValue('pointCategoryId', message.pointCategoryId);
      setValue('pointCategoryName', message.pointCategoryName);
      setValue('pointAwardedFlg', message.pointAwardedFlg);

      // イベントタイプをセット
      onEventTypeSelect(message.eventType ?? '');

      // 時間の設定関係
      setValue('createAt', message.createAt);
      setValue('sendDate', message.sendDate);
      if((message.draftFlg || message.sendDate > new Date()) && !eventCreateFlg){
        setValue('sendDateTime', `${('00' + message.sendDate.getHours()).slice(-2)}:${('00' + message.sendDate.getMinutes()).slice(-2)}`);
      } else {
        setValue('sendDateTime', `--:--`);
      };
      if(message.eventFlg){
        setValue('eventAt', message.eventAt);
        setValue('eventEndAt', message.eventEndAt);
        if(!message.isEventTimeEmpty) {
          setValue('eventAtTime', `${('00' + message.eventAt.getHours()).slice(-2)}:${('00' + message.eventAt.getMinutes()).slice(-2)}`);
          setValue('eventEndAtTime', `${('00' + message.eventEndAt.getHours()).slice(-2)}:${('00' + message.eventEndAt.getMinutes()).slice(-2)}`);
        }else{
          setValue('eventAtTime', undefined);
          setValue('eventEndAtTime', undefined);
        };
      };
      if(message.publishEndAt !== undefined){
        setValue('publishEndAt', message.publishEndAt);
        setValue('publishEndAtTime', `${('00' + message.publishEndAt?.getHours()).slice(-2)}:${('00' + message.publishEndAt?.getMinutes()).slice(-2)}`);
      } else {
        setValue('publishEndAt', undefined);
        setValue('publishEndAtTime', undefined);
      };

      // 配信先グループのセット
      setValue('topic', message.topic);
      setTopicState(message.topic ?? []);
      const tokenStateInit = tokenList?.filter((token) => token.messageId === message.id)?? [];
      if(message.topic !== undefined && (message.topic.includes(company?.displayId!) || message.topic.includes('all'))) {
        select.setValue(company?.displayId ?? 'all');
      } else if(select.value === "token" || message.select === "token" || tokenStateInit.length > 0 ){
        select.setValue('token');
      }else if(select.value === "topic" || (message.topic !== undefined && message.topic.length > 0) || message.select === 'topic'){
        select.setValue('topic');
      } else {
        select.setValue('');
      }

      // 返信ボタンの初期データの選択 通常タイプとイベントタイプ
      const replyButtonList = message.eventFlg ? eventReplyButtonList : defaultReplyButtonList;
      setReplyButtonList(replyButtonList);

      setQuestionnaireData(getQuestionnaire);
    };
  }, [message]);

  useEffect(() => {
    // 新規の場合
    if(!message.draftFlg && message.sendDate <= new Date() && open) {
      // 時間の設定関係
      onOnlineUrlSelect('none');
      setValue('createAt', new Date());
      setValue('sendDate', new Date());
      // アンケートのリセット
      setValue('questionnaireFlg', false);
      setQuestionnaireData(undefined);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[message.draftFlg, open, setValue]);

  useEffect(() => {
    // 予定表掲載チェックボックスの値で返信ボタンの内容を入れ替え
    const replyButtonList = getValues("eventFlg") ? eventReplyButtonList : defaultReplyButtonList;
    setReplyButtonList(replyButtonList);
    if(replyButtonList.length > 0){
      setValue('replyButton', replyButtonSelected?.name ?? '');
    }
    if(!getValues("eventFlg") && eventFlgResult !== getValues("eventFlg")){
      // イベントのチェックを外した時にイベント関連の値をリセット
      const now = new Date();
      const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      onReplyButtonSelect('undefined');
      onEventTypeSelect("");
      setValue('eventAt', nowDate);
      setValue('eventEndAt', nowDate);
      setValue('eventAtTime', undefined);
      setValue('eventEndAtTime', undefined);
      setValue('eventVenue', "");
      onOnlineUrlSelect('none');
      setValue('questionnaireFlg', false);
      setQuestionnaireData(undefined);
      if (questionnaireModalRef.current) {
        questionnaireModalRef.current.resetForm();
      }
      setEventFlgResult(getValues("eventFlg"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[watch("eventFlg")]);

  useEffect(() => {
    // イベントタイプのセレクトボックスの変更を監視して値を入れ替え
    setValue('eventType', selectedEventType);
  },[selectedEventType, setValue, onEventTypeSelect]);

  useEffect(() => {
    // 返信ボタンのセレクトボックスの変更を監視して値を入れ替え
    setValue('replyButton', replyButtonSelected?.name ?? '');
    if(replyButtonSelected?.name == "予定表の出欠" && questionnaireData){
      questionnaireData.target = "全員";
      setQuestionnaireData(questionnaireData);
    }
  },[replyButtonSelected, questionnaireData, setValue, onReplyButtonSelect]);

  useEffect(() => {
    // ミーティングURLのセレクトボックスの変更を監視して値を入れ替え
    setValue('link', onlineUrlSelected ?? '');
    if(onlineUrlSelected === '' && (message.url === undefined || message.url === '')){
      setValue('url', 'https://');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[onlineUrlSelected, setValue, onOnlineUrlSelect]);

  useEffect(() => {
    // 登録後のフォームを初期化(お知らせ新規作成)
    submitProcessing.current = false; 
    if(!open && !(message.draftFlg || message.sendDate > new Date())) {
      // 全体
      reset();
      // 配信先選択のリセット
      select.setValue('');
      // 配信時間のリセット
      setValue('sendDateTime', '');
      // アンケートのリセット
      setValue('questionnaireFlg', false);
      setQuestionnaireData(undefined);
      // ポイント設定のリセット
      setValue('pointSettingFlg', false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[company?.displayId, open, reset]);

  useEffect(() => {
    // topic 全体送信選択時
    if (select.value === company?.displayId) {
      setValue('topic', [select.value]);
    } else {
      setValue('topic', []);
    };
    setValue('select', select.value);
  },[company?.displayId, select.value, setValue]);

  useEffect(() => {
    if(select.value === 'topic') {
      setValue('topicLength', selectedRowList.length)
    }
    if(select.value === 'token') {
      setValue('tokenLength', selectedRowList.length)
    }
  },[selectedRowList, setValue, select.value])

  // イベントタイプ作成
  const eventTypeList = company?.eventType?.map((item) => {
    return {
      id: item,
      name: item,
      value: item
    }
  }) ?? [];
  eventTypeList.unshift({id: 'undefined', name: '', value: ''});

  // 時分選択用の項目作成
  const timeSelectList: string[] = [];
  for(let i = 0; i < 2400; i += 15) {
    const time = ('0000' + i).slice(-4);
    timeSelectList.push(`${time.substring(0,2)}:${time.substring(2)}`);
    if(time.lastIndexOf('45', 4) > 0) i += 40;
  };

  // 配信日 日付け選択後に時分を設定
  const onChangeDate = (e: any) => {
    const dateTime = new Date(e.target.value);
    const name = e.target.name;
    const timeName: any = `${name}Time`;
    const nowHoursString = (new Date().getHours()).toString().padStart(2, '0');
    const nowMinutsString = (new Date().getMinutes()).toString().padStart(2, '0');
    const value = getValues(timeName) === '' || getValues(timeName) === '--:--'
      ? `${nowHoursString}:${nowMinutsString}`
      : getValues(timeName) ?? '00:00';
    dateTime.setHours(Number(value.substring(0, 2)));
    dateTime.setMinutes(Number(value.substring(3)));
    dateTime.setSeconds(Number(0));
    setValue(name, dateTime);
    if (name === "sendDate") {
      const dateTimeDate = new Date(
        dateTime.getFullYear(),
        dateTime.getMonth(),
        dateTime.getDate()
      )
      const nowDate = new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        new Date().getDate()
      )
      if (dateTimeDate.getTime() === nowDate.getTime() && (getValues(timeName) === undefined || getValues(timeName) === '' || getValues(timeName) === '--:--')) {
        // 配信日が処理日かつ配信時間が空欄の場合
        dateTime.setHours(Number(nowHoursString));
        dateTime.setMinutes(Number(nowMinutsString));
        dateTime.setSeconds(Number(0));
        setValue(name, dateTime);
      }
    } else if (name === "publishEndAt" && Number.isNaN((new Date(dateTime)).getTime())) {
      // 掲載終了日が空欄の場合
      setValue(name, undefined);
    }
    if(submitProcessing.current === true){
      // 配信日入力チェック
      trigger("sendDate");
      // 配信時間入力チェック
      trigger("sendDateTime");
      // 掲載終了日入力チェック
      trigger("publishEndAt");
      // イベント開催日入力チェック
      trigger("eventAt");
      // 開始時間入力チェック
      trigger("eventAtTime");
    }
  };

  // イベント開催日 日付け選択後に時分を設定
  const onEventChangeDate = (e: any) => {
    const dateTime = new Date(e.target.value);
    const name = e.target.name;

    const nowHoursString = (new Date().getHours()).toString().padStart(2, '0');
    const nowMinutsString = (new Date().getMinutes()).toString().padStart(2, '0');

    // 開始時間の設定
    const timeName: any = `${name}Time`;
    const value = getValues(timeName) === '' || getValues(timeName) === '--:--'
      ? `${nowHoursString}:${nowMinutsString}`
      : getValues(timeName) ?? '00:00';
    dateTime.setHours(Number(value.substring(0, 2)));
    dateTime.setMinutes(Number(value.substring(3)));
    dateTime.setSeconds(Number(0));
    setValue(name, dateTime);

    // 終了時間の設定
    const endTimeName: any = `eventEndAtTime`;
    const endValue = getValues(endTimeName) === '' || getValues(endTimeName) === '--:--'
      ? `${nowHoursString}:${nowMinutsString}`
      : getValues(endTimeName) ?? '00:00';
    dateTime.setHours(Number(endValue.substring(0, 2)));
    dateTime.setMinutes(Number(endValue.substring(3)));
    dateTime.setSeconds(Number(0));
    setValue('eventEndAt', dateTime);

    if(submitProcessing.current === true){
      // イベント開催日入力チェック
      trigger(name);
      // 開始時間入力チェック
      trigger("eventAtTime");
      // 掲載終了日入力チェック
      trigger("publishEndAt");
    }
  };

  // 開始時間、終了時間 時分選択後に日時フィールドに反映
  const onChangeTime = (e: any) => {
    const id = e.target.name.toString().replace('Time', '');
    const idDate = new Date(getValues(id));
    if(e.target.value !== null && typeof(e.target.value) === 'string'){
      const value = e.target.value;
      idDate.setHours(Number(value.substring(0, 2)));
      idDate.setMinutes(Number(value.substring(3)));
      idDate.setSeconds(Number(0));
      if(id === 'sendDate' && value === ''){
        idDate.setHours(new Date().getHours());
        idDate.setMinutes(new Date().getMinutes());
        idDate.setSeconds(new Date().getSeconds());
      };
      setValue(id, idDate);
      setValue(e.target.name, e.target.value);
    };
    if(submitProcessing.current === true){
      // 開始時間、終了時間入力チェック
      trigger(e.target.name);
      // 開始時間変更時、終了時間入力チェック
      if(e.target.name === "eventAtTime"){
      	trigger("eventEndAtTime");
      }
    }
  };

  const onChangeFlg = (e: any) => {
    setValue(e.target.name, !getValues(e.target.name));
  };

  useEffect(() => {
    if(!getValues('urlButtonFlg')){
      setValue('urlLink', "");
      setValue('urlButtonName', "");
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[watch("urlButtonFlg")]);

  return (
    <Modal
      open={open}
      toggle={toggle}
      closeOnClickOutside={closeOnClickOutside}
    >
      <div className="overflow-x-auto">
        <Dialog.Panel className="transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all w-[calc(100vw_-_25rem)] min-w-[1024px] max-w-[1280px]">
          <form
            onSubmit={handleSubmit(onValidSubmit)}
            noValidate
          >
            <div className="p-4">
              <h3 className="text-lg font-medium leading-6 text-gray-800">
                {!getValues("eventFlg") ? 'お知らせ' : '予定'}{getValues('draftFlg') ? '編集' : '作成'}
              </h3>
            </div>
            <div className="overflow-y-auto px-8 text-left pb-8 h-fit max-h-[calc(100vh_-_11rem)] sm:max-h-[calc(100vh_-_10rem)]">
              <FormBox gridcol={"grid-cols-2"} className={'grid !pt-[0px] !gap-8'}>
                <div className="flex flex-col gap-5">
                  <FormController
                    control={control}
                    name={"title"}
                    label={"タイトル"}
                    type={"text"}
                    className="h-[4.5rem]"
                    required
                  />
                  {getValues("eventFlg") && (
                    <>
                      <div className="grid grid-cols-3 items-start gap-4">
                        <FormController
                          control={control}
                          name={"eventAt"}
                          label={"イベント開催日"}
                          type={"date"}
                          onChange={onEventChangeDate}
                          errMsgWhitespaceWrap
                          required
                        />
                        <FormController
                          control={control}
                          name={"eventAtTime"}
                          label={"開始時間"}
                          type={"time"}
                          step={900}
                          min={'00:00'}
                          max={'23:45'}
                          list={'eventAtItem'}
                          onChange={onChangeTime}
                          errMsgWhitespaceWrap
                          required
                        />
                        <datalist id={'eventAtItem'}>
                          {timeSelectList.map((time, index) => {
                            return (<option key={`eventAtItem${time}-${index}`} value={time}></option>)
                          })}
                        </datalist>
                        <FormController
                          control={control}
                          name={"eventEndAtTime"}
                          label={"終了時間"}
                          type={"time"}
                          step={900}
                          min={'00:00'}
                          max={'23:45'}
                          list={'eventEndAtItem'}
                          onChange={onChangeTime}
                          errMsgWhitespaceWrap
                          required
                        />
                        <datalist id={'eventEndAtItem'}>
                          {timeSelectList.map((time, index) => {
                            return (<option key={`eventEndAtItem${time}-${index}`} value={time}></option>)
                          })}
                        </datalist>
                      </div>
                      <FormController
                        control={control}
                        name={"eventVenue"}
                        label={"会場"}
                        type={"text"}
                        className="h-[4.5rem]"
                      />
                    </>
                  )}
                  <Controller
                    name="body"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        type="textarea"
                        label="本文"
                        className={`h-[22rem] pb-11`}
                        error={fieldState.error}
                        helperText={fieldState.error?.message}
                        required
                      />
                    )}
                  />
                  <div className={"flex flex-col gap-1"}>
                    <Label id="" className="self-start block text-sm font-medium text-gray-700">画像添付</Label>
                    <div className={`flex items-center justify-center${base64Image === '' ? " gap-1 flex-col" : " gap-2"}`}>
                      {base64Image === ''
                        ? (<>
                          <InputFile
                            id={'image'}
                            size={"sm"}
                            color={"indigo"}
                            className={"items-center min-w-[132px] h-8"}
                            children={(<>
                              画像を選択
                            </>)}
                            onChange={(e) => {
                              const files = e.target.files;
                              if(files && files[0]){
                                onSendImage(files[0]);
                              }
                            }}
                          />
                          <span className="text-sm font-medium text-gray-500 items-center justify-center">
                            画像は<span className="text-red-500">10MB</span>以下、1ファイルのみです。
                            また、画像形式は<span className="text-red-500">JPEG・PNG</span>のみです。
                          </span>
                        </>)
                        : (<>
                          <img alt={'添付画像'} src={base64Image} className={"max-h-[16rem] max-w-[16rem]"}/>
                          <Button
                            onClick={() => setBase64Image('')}
                            outline={true}
                            children={'キャンセル'}
                            size={"sm"}
                            className={"w-[132px] h-8"}
                          />
                        </>)
                      }
                    </div>
                  </div>

                  <FormBox
                    gridcol="grid-cols-1"
                    className={'p-4 border border-gray-300 rounded-md shadow-sm bg-yellow-50'}>
                    <div className={'flex items-center'}>
                      <Label id={"urlButtonFlg"} className={'mr-4'}>
                        URLボタンあり
                      </Label>
                      <Controller
                        name={"urlButtonFlg"}
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            type="checkbox"
                            className={'h-6'}
                            onChange={onChangeFlg}
                            error={fieldState.error}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    </div>
                    {getValues("urlButtonFlg") && (<>
                      <FormController
                        control={control}
                        name={"urlButtonName"}
                        label={"URLボタン名"}
                        type={"text"}
                        className={"w-1/2"}
                        required={getValues("urlButtonFlg")}
                      />
                      <FormController
                        control={control}
                        name={"urlLink"}
                        label={"URL"}
                        type={"text"}
                        required={getValues("urlButtonFlg")}
                      />
                    </>)}
                  </FormBox>
                  <FormController
                    control={control}
                    name={"contactInformation"}
                    label={"問い合わせ先"}
                    type={"text"}
                  />
                </div>
                <div className="flex flex-col gap-5">
                  <Controller
                    name={"replyButton"}
                    control={control}
                    render={({ field, fieldState }) => (
                    <SelectFields
                      {...field}
                      label="返信ボタン"
                      color="indigo"
                      className="h-[4.5rem]"
                      selectClassName="px-3 py-2 h-auto"
                      onChange={(e: any) => {onReplyButtonSelect(e.target.value);field.onChange(e)}}
                      value={replyButtonSelected?.id}
                      selectItems={replyButtonList?.map((item) => {
                        return {
                          id: item.id,
                          name: item.id !== "undefined"
                            ? `${item.name}: ${item.buttonName.join(" / ")}`
                            : item.name
                        }
                      })}
                      error={fieldState.error}
                      helperText={fieldState.error?.message}
                      required={getValues('eventFlg')}
                    />
                    )}
                  />
                  {getValues("eventFlg") && (
                    <>
                      <Controller
                        name={"link"}
                        control={control}
                        render={({ field, fieldState }) => (
                          <SelectFields
                            {...field}
                            id="link"
                            label="ミーティングURL"
                            color="indigo"
                            selectClassName="px-3 py-2 h-auto"
                            onChange={(e: any) => {onOnlineUrlSelect(e.target.value);}}
                            value={onlineUrlSelected}
                            className={""}
                            selectItems={onlineUrlList?.map((item) => {
                              return {
                                id: item.id,
                                name: item.id !== "none" && item.id !== 'input'
                                  ? `${item.name}: ${item.url}`
                                  : item.name,
                                value: item.url
                              }
                            })}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                      {(onlineUrlSelected === '')
                        ? (<FormController
                            control={control}
                            name={"url"}
                            label={""}
                            type={"url"}
                            className={'h-[4.5rem]'}
                          />)
                        : (<></>)
                      }
                      <Controller
                        name={"eventType"}
                        control={control}
                        render={({ field, fieldState }) => (
                          <SelectFields
                            {...field}
                            label="イベントタイプ"
                            color="indigo"
                            selectClassName="px-3 py-2 h-auto"
                            onChange={(e: any) => {onEventTypeSelect(e.target.value);field.onChange(e)}}
                            value={selectedEventType}
                            className={"!pt-[0px] h-[4.5rem]"}
                            selectItems={eventTypeList}
                            error={fieldState.error}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                      {optionFlg.point && (
                        <FormBox gridcol={"grid-cols-1"} className={"text-sm"}>
                          {/* ポイント設定ラベルと設定ボタン */}
                          <hr className="border-gray-300" />
                          <div className="flex items-center justify-between w-full">
                            <Label id="" className="block text-sm font-medium text-gray-700">ポイント</Label>
                            {!pointSettingFlg && (
                              <Button size="sm" className={"w-[132px] ml-2 h-8 flex items-center justify-center"} onClick={openMessagePointSettingModal} children={"設定"} />
                            )}
                          </div>
                          {pointSettingFlg && (
                            <>
                              <div>
                                <p>ポイント名　　：{getValues("pointName")}</p>
                                <p>ポイントタイプ：{getValues("pointTypeName")}</p>
                                <p>ポイント数　　：{getValues("pointAssignmentPoint")}</p>
                              </div>
                              <div className="flex justify-end gap-[10px] w-full">
                                <Button size="sm" className={"w-[132px] h-8 flex items-center justify-center"} onClick={openMessagePointSettingModal}>変更</Button>
                                <Button
                                  onClick={handlePointDelete}
                                  outline={true}
                                  className={`bg-white w-[132px] h-8 flex items-center justify-center`}
                                  size={"sm"}
                                >キャンセル</Button>
                              </div>
                            </>
                          )}
                          <MessagePointSettingModal
                            ref={pointModalRef}
                            open={isMessagePointSettingModalOpen}
                            toggle={closeMessagePointSettingModal}
                            closeOnClickOutside
                            onSubmit={handlePointSettingSubmit} 
                            children={undefined}
                            point={{
                              id: getValues("pointId") || "",
                              categoryId: getValues("pointCategoryId") || 0,
                              categoryName: getValues("pointCategoryName") || "",
                              typeId: getValues("pointTypeId") || "",
                              typeName: getValues("pointTypeName") || "",
                              pointName: getValues("pointName") || "",
                              point: getValues("pointAssignmentPoint") || 0
                            }}              
                          />
                        </FormBox>
                      )}
                      <hr className="border-gray-300" />
                      <FormBox gridcol={"grid-cols-1"} className={"!pt-[0px] !gap-2 text-sm"}>
                        {/* アンケートラベルと設定ボタン */}
                        <div className="flex items-center justify-between w-full">
                          <Label id="" className="block text-sm font-medium text-gray-700">アンケート</Label>
                          {!questionnaireData && (
                            <Button size="sm" className={"w-[132px] ml-2 h-8 flex items-center justify-center"} onClick={openQuestionnaireModal} children={"設定"} />
                          )}
                        </div>
                        {questionnaireData ? (
                          <>
                            <div>
                              <p>対象者：{questionnaireData.target}</p>
                              <p>件名　：{questionnaireData.subject}</p>
                              {questionnaireData.questionNumber >= 1 && questionnaireData.title1 && (
                                <div>
                                  <h4>【質問１】</h4>
                                  <p>　{questionnaireData.title1}</p>
                                  <p>　{questionnaireData.answers1.filter(answer1 => answer1).join(" / ")}</p>
                                </div>
                              )}
                              {questionnaireData.questionNumber >= 2 && questionnaireData.title2 && (
                                <div>
                                  <h4>【質問２】</h4>
                                  <p>　{questionnaireData.title2}</p>
                                  <p>　{questionnaireData.answers2.filter(answer2 => answer2).join(" / ")}</p>
                                </div>
                              )}
                              {questionnaireData.questionNumber >= 3 && questionnaireData.title3 && (
                                <div>
                                  <h4>【質問３】</h4>
                                  <p>　{questionnaireData.title3}</p>
                                  <p>　{questionnaireData.answers3.filter(answer3 => answer3).join(" / ")}</p>
                                </div>
                              )}
                            </div>
                            <div className="flex justify-end gap-[10px] w-full">
                              <Button size="sm" className={"w-[132px] h-8 custom-width flex items-center justify-center"} onClick={openQuestionnaireModal} children={"変更"} />
                              <Button
                                onClick={handleQuestionnaireDelete}
                                outline={true}
                                className={`bg-white w-[132px] h-8 custom-width flex items-center justify-center`}
                                children={'キャンセル'}
                                size={"sm"}
                              ></Button>
                            </div>
                          </>
                        ) : null}
                        <QuestionnaireModal
                          ref={questionnaireModalRef}
                          open={isQuestionnaireModalOpen}
                          toggle={closeQuestionnaireModal}
                          closeOnClickOutside={true}
                          onSubmit={handleQuestionnaireSubmit}
                          questionnaire={questionnaireData} 
                          replyButtonSelected={replyButtonSelected?.name} 
                          children={undefined}                        
                        />
                      </FormBox>
                      <hr className="border-gray-300" />
                      <div ref={modalContentRef} tabIndex={-1}></div>
                    </>
                  )}
                  <div className="grid grid-cols-3 items-start gap-4">
                    <FormController
                      control={control}
                      name={"sendDate"}
                      label={"配信日"}
                      type={"date"}
                      onChange={onChangeDate}
                      errMsgWhitespaceWrap
                      required
                    />
                    <FormController
                      control={control}
                      name={"sendDateTime"}
                      label={"配信時間"}
                      type={"time"}
                      step={900}
                      min={'00:00'}
                      max={'23:45'}
                      list={'sendDateItem'}
                      onChange={onChangeTime}
                      errMsgWhitespaceWrap
                    />
                    <datalist id={'sendDateItem'}>
                      {timeSelectList.map((time, index) => {
                        return (<option key={`sendDateItem${time}-${index}`} value={time}></option>)
                      })}
                    </datalist>
                    <FormController
                      control={control}
                      name={"publishEndAt"}
                      label={"掲載終了日"}
                      onChange={onChangeDate}
                      type={"date"}
                      errMsgWhitespaceWrap
                    />
                  </div>
                  <div className="grid items-start">
                    <Controller
                      name="select"
                      control={control}
                      render={({ field, fieldState }) => (
                        <RadioButtonMessage
                          vertical
                          {...field}
                          label={'配信先'}
                          children={undefined}
                          value={select}
                          radioButtonItem={radioItem}
                          className={"flex items-center !space-y-0 space-x-10"}
                          error={fieldState.error}
                          helperText={select.value === "" || (selectedRowList.length === 0 && (select.value === "token" || select.value === "topic")) ? fieldState.error?.message : ""}
                          selectedColumns={selectedColumns}
                          selectedRowList={selectedRowList}
                          required
                        />
                      )}
                    />
                    {select.value === "topic" && (
                      <Button
                        onClick={() => setIsGroupSelectOpen(true)}
                        className={'mt-2 justify-self-end'}
                        children={'グループ選択'}
                        size={"sm"}
                        rounded
                      />
                    )}
                    {select.value === "token" && (
                      <Button
                        onClick={() => setIsUserSelectOpen(true)}
                        className={'mt-2 justify-self-end'}
                        children={'利用者選択'}
                        size={"sm"}
                        rounded
                      />
                    )}
                  </div>
                  {(selectedRowList.length >= 1 && (select.value === "token" || select.value === "topic")) && (
                    <FormBox gridcol={'grid-cols-1'} className={"!pt-[0px] break-all"}>
                      <TableList
                      gridCol={selectedColumns}
                      gridRow={selectedRowList}
                      sort={false}
                      footer={false}
                      />
                    </FormBox>
                  )}
                </div>
              </FormBox>
            </div>
            <ModalFoterBox>
              <Button
                className="mx-2 mt-1 w-[132px]"
                outline
                onClick={() => {
                  if(!message.draftFlg){
                    setTopicState([]);
                    setTokenState([]);
                  }
                  reset();
                  toggle(true);
                }}
                children={'キャンセル'}
              />
              <Button
                className="mt-1 mr-2 w-[132px]"
                onClick={async () => {
                  const formValues = getValues();
                  if (formValues.pointSettingFlg) {
                    const pointEnabled = await checkOption("point", dispatch, false);
                    if (!pointEnabled) {
                      dispatch(
                        alertOpen(
                          AlertValue(
                            "ポイント管理のオプションが無効のため、ポイントを設定できません。",
                            "danger"
                          )
                        )
                      );
                    }else{
                      onDraft(formValues);
                    }
                  } else {
                    onDraft(formValues);
                  }
                }}
                color={'violet'}
                children={'下書き保存'}
              />
              <Button
                className="mt-1 w-[132px]"
                submit
                onClick={() => submitProcessing.current = true}
                children={'確認'}
              />
            </ModalFoterBox>
          </form>
        </Dialog.Panel>
      </div>
    </Modal>
  );
};